import {
    useEffect
} from "react";
import {
    Animated,
    Platform
} from "react-native";
import type {
    SkeletonClockHookConstantType,
    SkeletonClockConstantType,
    SkeletonClock
} from "./type";
import {
    MOTION_EASINGS
} from "../../helpers/motion";

const SKELETON_CLOCKS = new Map<string, SkeletonClock>();

const createSkeletonClock = ({
    duration,
    kind
}: SkeletonClockConstantType): SkeletonClock => {
    const value = new Animated.Value(0);

    let animation: Animated.CompositeAnimation | undefined;

    let retainCount = 0;

    const createAnimation = () => {
        const useNativeDriver = Platform.OS !== "web";

        if(kind === "shimmer") {
            return Animated.loop(Animated.timing(value, {
                easing: MOTION_EASINGS.linear,
                isInteraction: false,
                useNativeDriver,
                toValue: 1,
                duration
            }));
        }

        return Animated.loop(Animated.sequence([
            Animated.timing(value, {
                easing: MOTION_EASINGS.inOut,
                duration: duration / 2,
                isInteraction: false,
                useNativeDriver,
                toValue: 1
            }),
            Animated.timing(value, {
                easing: MOTION_EASINGS.inOut,
                duration: duration / 2,
                isInteraction: false,
                useNativeDriver,
                toValue: 0
            })
        ]));
    };

    const retain = () => {
        retainCount += 1;

        if(retainCount > 1) {
            return;
        }

        value.setValue(0);

        animation = createAnimation();

        animation.start();
    };

    const release = () => {
        retainCount = Math.max(0, retainCount - 1);

        if(retainCount) {
            return;
        }

        animation?.stop();

        animation = undefined;
    };

    return {
        release,
        retain,
        value
    };
};

export const getSkeletonClock = ({
    duration,
    kind
}: SkeletonClockConstantType): SkeletonClock => {
    const clockKey = `${kind}-${duration}`;

    const cachedClock = SKELETON_CLOCKS.get(clockKey);

    if(cachedClock) {
        return cachedClock;
    }

    const clock = createSkeletonClock({
        duration,
        kind
    });

    SKELETON_CLOCKS.set(clockKey, clock);

    return clock;
};

export const useSkeletonClock = ({
    duration,
    kind
}: SkeletonClockHookConstantType): Animated.Value | undefined => {
    const clock = kind === "none" || !duration ? undefined : getSkeletonClock({
        duration,
        kind
    });

    useEffect(() => {
        if(!clock) {
            return;
        }

        clock.retain();

        return () => {
            clock.release();
        };
    }, [
        clock
    ]);

    return clock?.value;
};
